import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  IconButton,
  Button,
  Box,
  CircularProgress,
  Typography,
} from "@mui/material";
import LanguageIcon from "@mui/icons-material/Language";
import InputCst from "@components/InputCst/InputCst";
import { LanguageSelectorProps } from "@components/InputCst/LanguageSelector/types";
import { useFetchDataDetail } from "@hooks/useFetchDetail";
import { ServicesURL } from "@utils/utilsApi";
import useSWRMutation from "swr/mutation";
import { MethodType } from "../../../../services/type";
import { fetcher } from "../../../../services/config";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import FormLoadingStateCst from "@components/FormLoadingStateCst";

const LanguageSelectorObjectLearning: React.FC<LanguageSelectorProps> = ({
  languages,
  col,
  id,
  idObject,
  disabled,
}) => {
  const { t, i18n } = useTranslation(["form"]);
  const [isLangPopupOpen, setLangPopupOpen] = useState(false);
  const [submitState, setSubmitState] = useState<{
    loading: boolean;
    submitted: boolean;
  }>({ loading: false, submitted: false });
  const [fieldStates, setFieldStates] = useState<Record<any, any>>({});
  const [selectedKey, setSelectedKey] = useState<string>("");
  const [selectedEntityType, setSelectedEntityType] =
    useState<string>("learning_object");

  // RETRIEV DATA
  const { dataDetail: translations } = useFetchDataDetail(
    selectedKey
      ? `${ServicesURL.getTranslations}?idObject=${idObject}&corporateId=${id}&entityType=${selectedEntityType}&propertyName=${selectedKey}`
      : null,
  );

  useMemo(() => {
    if (translations?.propertiesTranslated) {
      const updatedFieldStates = Object.entries(
        translations?.propertiesTranslated,
      ).reduce(
        (acc: any, [lang, value]) => {
          if (
            typeof value === "object" &&
            value !== null &&
            "translatedValue" in value
          ) {
            acc[lang] = (value as { translatedValue: string }).translatedValue;
          }
          return acc;
        },
        {} as Record<string, string>,
      );

      setFieldStates(updatedFieldStates);
    }
  }, [translations, selectedKey]);

  const { trigger } = useSWRMutation(
    ServicesURL.postTranslations,
    (url: string, arg: { arg: any }) =>
      fetcher(`${url}`, MethodType.PUT, arg.arg),
  );

  const handleLangIconClick = () => {
    setLangPopupOpen(true);
    setSelectedKey(col.accessorKey); // ES: title
    setSelectedEntityType(col.entityType); // ES: learning_object
  };

  const handleLangPopupClose = () => {
    setLangPopupOpen(false);
  };

  const handleFieldChange = (langId: string, value: string) => {
    setFieldStates((prev) => ({
      ...prev,
      [langId]: value,
    }));

    setSubmitState((prev) => ({
      ...prev,
      [langId]: { loading: false, submitted: false },
    }));
  };

  const handleSubmit = async () => {
    const transformed = Object.entries(fieldStates).reduce(
      (acc, [lang, value]) => {
        acc[lang] = {
          propertyName: selectedKey,
          translatedValue: value.trimStart().trimEnd(),
        };
        return acc;
      },
      {} as Record<string, { propertyName: string; translatedValue: string }>,
    );

    if (fieldStates && Object.entries(fieldStates).length > 0) {
      setFieldStates((current) =>
        Object.fromEntries(
          Object.entries(current).map(([key, value]) => [
            key,
            value.trimStart().trimEnd(),
          ]),
        ),
      );
    }

    const payload = {
      idObject: idObject,
      entityType: col.entityType,
      corporateId: id,
      propertiesTranslated: transformed,
    };

    setSubmitState({ loading: true, submitted: false });

    try {
      await trigger(payload);
      setSubmitState({ loading: false, submitted: true });
      handleLangPopupClose();
      console.log("Submitted successfully");
    } catch (error) {
      console.error("Error during submission:", error);
      setSubmitState({ loading: false, submitted: false });
    }
  };

  return (
    <>
      <IconButton onClick={handleLangIconClick} edge="end" disabled={disabled}>
        <LanguageIcon />
      </IconButton>
      <Dialog
        open={isLangPopupOpen}
        onClose={handleLangPopupClose}
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            maxWidth: "100%",
            "@media (min-width:600px)": {
              maxWidth: "80%",
            },
            "@media (min-width:960px)": {
              maxWidth: "70%",
            },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "10px",
            paddingRight: "16px",
          }}
        >
          <Typography variant="h6" component="div">
            {col.header || col.label}
          </Typography>
          <IconButton onClick={handleLangPopupClose}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {!translations ? (
            <FormLoadingStateCst />
          ) : (
            <Grid
              container
              spacing={2}
              sx={{
                marginY: "20px",
              }}
            >
              {languages &&
                languages.secondary_languages.map((lang: any) => {
                  const fieldValue = fieldStates[lang.id] || "";

                  return (
                    <Grid
                      item
                      xs={12}
                      key={`${col.accessorKey}-${lang.id}`}
                      sx={{
                        marginY: "5px",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 2,
                        }}
                      >
                        <InputCst
                          id={`${col.accessorKey}-${lang.id}`}
                          label={lang.label}
                          value={fieldValue}
                          onChange={(e) => {
                            const value = e.target.value;
                            handleFieldChange(lang.id, value);
                          }}
                          maxValue={col?.rules?.max?.value}
                          multiline={col.multiline}
                          rows={col.rows}
                          textTooltip={col?.tooltip}
                          tooltipFile={col?.tooltipFile}
                          tooltipFileIta={col?.tooltipFileIta}
                          tooltipFileEn={col?.tooltipFileEn}
                          type={col.inpuType || "text"}
                          sx={{ flex: 1 }}
                        />
                      </Box>
                    </Grid>
                  );
                })}
            </Grid>
          )}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: "20px",
            }}
          >
            <Button variant="contained" color="primary" onClick={handleSubmit}>
              {submitState.loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                t("saveButton")
              )}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LanguageSelectorObjectLearning;
